import type { PieceType } from "../core/model";

interface PieceStyle {
  name: string;
  base: string;
  dark: string;
  light: string;
  accent: string;
  glow: string;
}

export interface PieceDrawOptions {
  highContrast?: boolean;
  phase?: number;
  active?: boolean;
  ghost?: boolean;
  squash?: number;
}

export const PIECE_STYLES: Record<PieceType, PieceStyle> = {
  1: {
    name: "Blue Bat",
    base: "#2467f4",
    dark: "#0d2b8d",
    light: "#8ed6ff",
    accent: "#081b58",
    glow: "rgba(36,103,244,0.42)"
  },
  2: {
    name: "Red Cyclops Crab",
    base: "#ef3f2f",
    dark: "#951610",
    light: "#ffc7b5",
    accent: "#3b0907",
    glow: "rgba(239,63,47,0.42)"
  },
  3: {
    name: "Green Slime",
    base: "#55c96d",
    dark: "#1f7138",
    light: "#dcffd7",
    accent: "#0f3d21",
    glow: "rgba(85,201,109,0.42)"
  },
  4: {
    name: "Yellow Puffy Orb",
    base: "#f4cc4f",
    dark: "#966d0c",
    light: "#fff7b6",
    accent: "#5a3b05",
    glow: "rgba(244,204,79,0.42)"
  },
  5: {
    name: "Transparent Purple Squeeze",
    base: "#a878ff",
    dark: "#53308e",
    light: "#f1e8ff",
    accent: "#301957",
    glow: "rgba(168,120,255,0.45)"
  },
  6: {
    name: "Cream Skeleton",
    base: "#e6dcc6",
    dark: "#8f846e",
    light: "#fff8e7",
    accent: "#252116",
    glow: "rgba(248,236,205,0.36)"
  }
};

function normalizeOptions(options: PieceDrawOptions | boolean): PieceDrawOptions {
  return typeof options === "boolean" ? { highContrast: options } : options;
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function fillStroke(ctx: CanvasRenderingContext2D, fill: CanvasGradient | string, stroke: string, width = 2.8): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = width;
  ctx.strokeStyle = stroke;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.stroke();
}

function pieceGradient(ctx: CanvasRenderingContext2D, style: PieceStyle, top = 8, bottom = 58): CanvasGradient {
  const gradient = ctx.createLinearGradient(14, top, 48, bottom);
  gradient.addColorStop(0, style.light);
  gradient.addColorStop(0.18, style.base);
  gradient.addColorStop(0.72, style.base);
  gradient.addColorStop(1, style.dark);
  return gradient;
}

function drawShadow(ctx: CanvasRenderingContext2D): void {
  ctx.save();
  ctx.globalAlpha = 0.32;
  ctx.fillStyle = "#000000";
  ctx.beginPath();
  ctx.ellipse(32, 57, 20, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawGloss(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const gloss = ctx.createLinearGradient(x, y, x + w, y + h);
  gloss.addColorStop(0, "rgba(255,255,255,0.70)");
  gloss.addColorStop(0.45, "rgba(255,255,255,0.20)");
  gloss.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = gloss;
  ctx.beginPath();
  ctx.ellipse(x + w * 0.42, y + h * 0.38, w * 0.36, h * 0.19, -0.55, 0, Math.PI * 2);
  ctx.fill();
}

function drawEye(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  pupilX = 0.16,
  pupilY = 0.15,
  mood: "normal" | "cyclops" | "bone" = "normal"
): void {
  ctx.save();
  ctx.fillStyle = "rgba(0,0,0,0.22)";
  ctx.beginPath();
  ctx.arc(x + r * 0.18, y + r * 0.24, r * 1.04, 0, Math.PI * 2);
  ctx.fill();

  const eye = ctx.createRadialGradient(x - r * 0.3, y - r * 0.36, r * 0.1, x, y, r * 1.1);
  eye.addColorStop(0, "#ffffff");
  eye.addColorStop(0.62, mood === "bone" ? "#e9f0f4" : "#f8fbff");
  eye.addColorStop(1, "#b8c8d2");
  ctx.fillStyle = eye;
  ctx.strokeStyle = "#111820";
  ctx.lineWidth = mood === "cyclops" ? 2.7 : 2.1;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = mood === "cyclops" ? "#2b0d13" : "#101820";
  ctx.beginPath();
  ctx.arc(x + r * pupilX, y + r * pupilY, r * 0.39, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.arc(x - r * 0.24, y - r * 0.26, r * 0.16, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawOvalEye(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  pupilX = 0.12,
  pupilY = 0.12
): void {
  ctx.save();
  ctx.fillStyle = "rgba(0,0,0,0.22)";
  ctx.beginPath();
  ctx.ellipse(x + rx * 0.14, y + ry * 0.2, rx * 1.05, ry * 1.04, 0, 0, Math.PI * 2);
  ctx.fill();

  const eye = ctx.createRadialGradient(x - rx * 0.35, y - ry * 0.36, Math.min(rx, ry) * 0.16, x, y, Math.max(rx, ry));
  eye.addColorStop(0, "#ffffff");
  eye.addColorStop(0.68, "#f9fbff");
  eye.addColorStop(1, "#bbc9d3");
  ctx.fillStyle = eye;
  ctx.strokeStyle = "#101820";
  ctx.lineWidth = 2.3;
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  const pupilRadius = Math.min(rx, ry) * 0.42;
  ctx.fillStyle = "#05070a";
  ctx.beginPath();
  ctx.arc(x + rx * pupilX, y + ry * pupilY, pupilRadius, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.arc(x - rx * 0.22, y - ry * 0.25, pupilRadius * 0.38, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawBoneLine(
  ctx: CanvasRenderingContext2D,
  style: PieceStyle,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  width = 5
): void {
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = style.accent;
  ctx.lineWidth = width + 2;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  ctx.strokeStyle = style.light;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  const angle = Math.atan2(y2 - y1, x2 - x1);
  const drawCap = (x: number, y: number): void => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);
    ctx.fillStyle = style.light;
    ctx.strokeStyle = style.accent;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.ellipse(0, -width * 0.24, width * 0.42, width * 0.34, 0, 0, Math.PI * 2);
    ctx.ellipse(0, width * 0.24, width * 0.42, width * 0.34, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  };

  drawCap(x1, y1);
  drawCap(x2, y2);
  ctx.restore();
}

function drawHighContrastMark(ctx: CanvasRenderingContext2D, type: PieceType): void {
  ctx.save();
  ctx.globalAlpha = 0.9;
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  if (type === 1) {
    ctx.beginPath();
    ctx.moveTo(13, 45);
    ctx.lineTo(50, 18);
    ctx.stroke();
  } else if (type === 2) {
    ctx.strokeRect(17, 34, 30, 7);
  } else if (type === 3) {
    ctx.beginPath();
    ctx.arc(32, 38, 12, 0, Math.PI * 2);
    ctx.stroke();
  } else if (type === 4) {
    ctx.beginPath();
    ctx.moveTo(17, 45);
    ctx.lineTo(48, 19);
    ctx.moveTo(17, 19);
    ctx.lineTo(48, 45);
    ctx.stroke();
  } else if (type === 5) {
    for (let i = 0; i < 3; i += 1) {
      ctx.beginPath();
      ctx.moveTo(22 + i * 8, 19);
      ctx.lineTo(18 + i * 8, 46);
      ctx.stroke();
    }
  } else {
    ctx.beginPath();
    ctx.moveTo(23, 18);
    ctx.lineTo(42, 46);
    ctx.moveTo(42, 18);
    ctx.lineTo(23, 46);
    ctx.stroke();
  }
  ctx.restore();
}

function drawBlueBat(ctx: CanvasRenderingContext2D, style: PieceStyle, pose: number): void {
  const flap = [-1.5, -4.5, 2.5][pose] ?? 0;
  const wingFill = pieceGradient(ctx, style, 0, 60);

  ctx.beginPath();
  ctx.moveTo(27, 31);
  ctx.bezierCurveTo(20, 14 + flap, 13, 2 + flap, 8, 7 + flap);
  ctx.bezierCurveTo(1, 19 + flap, 1, 45 + flap, 8, 57);
  ctx.bezierCurveTo(11, 46, 15, 38, 23, 33);
  ctx.bezierCurveTo(19, 37, 18, 44, 18, 50);
  ctx.bezierCurveTo(21, 42, 25, 36, 31, 35);
  ctx.closePath();
  fillStroke(ctx, wingFill, style.accent, 2.5);

  ctx.beginPath();
  ctx.moveTo(37, 31);
  ctx.bezierCurveTo(44, 14 + flap, 51, 2 + flap, 56, 7 + flap);
  ctx.bezierCurveTo(63, 19 + flap, 63, 45 + flap, 56, 57);
  ctx.bezierCurveTo(53, 46, 49, 38, 41, 33);
  ctx.bezierCurveTo(45, 37, 46, 44, 46, 50);
  ctx.bezierCurveTo(43, 42, 39, 36, 33, 35);
  ctx.closePath();
  fillStroke(ctx, wingFill, style.accent, 2.5);

  ctx.save();
  ctx.strokeStyle = "rgba(8,27,88,0.58)";
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(26, 31);
  ctx.lineTo(9, 8 + flap);
  ctx.moveTo(24, 34);
  ctx.lineTo(8, 56);
  ctx.moveTo(38, 31);
  ctx.lineTo(55, 8 + flap);
  ctx.moveTo(40, 34);
  ctx.lineTo(56, 56);
  ctx.stroke();
  ctx.strokeStyle = "rgba(142,214,255,0.32)";
  ctx.lineWidth = 1.1;
  ctx.beginPath();
  ctx.moveTo(21, 35);
  ctx.lineTo(12, 47);
  ctx.moveTo(43, 35);
  ctx.lineTo(52, 47);
  ctx.stroke();
  ctx.restore();

  const hornFill = pieceGradient(ctx, style, 2, 24);
  ctx.beginPath();
  ctx.moveTo(23, 15);
  ctx.bezierCurveTo(23, 7, 25, 2, 28, 2);
  ctx.bezierCurveTo(30, 7, 30, 12, 28, 16);
  ctx.closePath();
  fillStroke(ctx, hornFill, style.accent, 2.2);

  ctx.beginPath();
  ctx.moveTo(41, 15);
  ctx.bezierCurveTo(41, 7, 39, 2, 36, 2);
  ctx.bezierCurveTo(34, 7, 34, 12, 36, 16);
  ctx.closePath();
  fillStroke(ctx, hornFill, style.accent, 2.2);

  ctx.beginPath();
  ctx.ellipse(32, 28.8, 12.4, 13.6, 0, 0, Math.PI * 2);
  fillStroke(ctx, pieceGradient(ctx, style, 7, 47), style.accent, 3);
  drawGloss(ctx, 24, 13, 18, 14);
  drawOvalEye(ctx, 26, 26.5, 7.1, 8.4, pose === 1 ? 0.18 : 0.08, 0.16);
  drawOvalEye(ctx, 38, 26.5, 7.1, 8.4, pose === 2 ? -0.02 : 0.08, 0.16);

  for (const x of [29, 35]) {
    ctx.beginPath();
    roundedRect(ctx, x - 2.3, 37.5, 4.6, 7.2, 1.4);
    fillStroke(ctx, "#fff6e8", style.accent, 1.2);
  }
}

function drawRedCrab(ctx: CanvasRenderingContext2D, style: PieceStyle, pose: number): void {
  const clawLift = [0, -3, 2][pose] ?? 0;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const side of [-1, 1]) {
    for (let i = 0; i < 3; i += 1) {
      const rootOffset = [18.8, 20, 18.8][i];
      const rootY = [37.2, 42.8, 47.2][i];
      const footOffset = [23.2, 21.4, 25.4][i];
      const footY = [47.2, 54.2, 56.2][i];
      const startX = 32 + side * rootOffset;
      const footX = 32 + side * footOffset;
      ctx.strokeStyle = style.accent;
      ctx.lineWidth = 4.2;
      ctx.beginPath();
      ctx.moveTo(startX, rootY);
      ctx.lineTo(footX, footY);
      ctx.stroke();
      ctx.strokeStyle = style.base;
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(startX, rootY);
      ctx.lineTo(footX, footY);
      ctx.stroke();
    }

    ctx.strokeStyle = style.accent;
    ctx.lineWidth = 4.4;
    ctx.beginPath();
    ctx.moveTo(32 + side * 14, 34);
    ctx.lineTo(32 + side * 18, 22 + clawLift);
    ctx.lineTo(32 + side * 17, 15 + clawLift);
    ctx.stroke();
    ctx.strokeStyle = style.base;
    ctx.lineWidth = 2.6;
    ctx.beginPath();
    ctx.moveTo(32 + side * 14, 34);
    ctx.lineTo(32 + side * 18, 22 + clawLift);
    ctx.lineTo(32 + side * 17, 15 + clawLift);
    ctx.stroke();

    ctx.save();
    ctx.translate(32 + side * 17, 13 + clawLift);
    ctx.scale(side, 1);
    ctx.beginPath();
    ctx.moveTo(0, 11);
    ctx.bezierCurveTo(-8, 3, -8, -9, 1, -11);
    ctx.bezierCurveTo(0, -5, 3, -1, 7, 1);
    ctx.bezierCurveTo(8, -7, 14, -9, 16, -3);
    ctx.bezierCurveTo(14, 7, 8, 12, 1, 11);
    ctx.closePath();
    fillStroke(ctx, pieceGradient(ctx, style, 10, 39), style.accent, 2.4);
    ctx.strokeStyle = "rgba(59,9,7,0.58)";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(7, 1);
    ctx.quadraticCurveTo(10, -2, 15, -3);
    ctx.stroke();
    ctx.restore();
  }

  ctx.beginPath();
  ctx.ellipse(32, 38, 18.5, 14.8, 0, 0, Math.PI * 2);
  fillStroke(ctx, pieceGradient(ctx, style, 16, 52), style.accent, 3);
  drawGloss(ctx, 18, 24, 29, 15);
  drawOvalEye(ctx, 32, 30, 10.2, 10.4, pose === 1 ? 0.18 : pose === 2 ? -0.06 : 0.08, 0.12);
  ctx.restore();
}

function drawGreenSlime(ctx: CanvasRenderingContext2D, style: PieceStyle, pose: number): void {
  const wobble = [-1.5, 2.5, 0][pose] ?? 0;
  const gradient = pieceGradient(ctx, style, 10, 56);
  ctx.beginPath();
  ctx.moveTo(15, 47);
  ctx.bezierCurveTo(6, 38, 10, 22 + wobble, 23, 18 + wobble);
  ctx.bezierCurveTo(30, 8 + wobble, 45, 13 - wobble, 48, 25);
  ctx.bezierCurveTo(60, 31, 55, 51, 41, 53);
  ctx.bezierCurveTo(36, 59, 27, 57, 23, 52);
  ctx.bezierCurveTo(20, 55, 17, 53, 15, 47);
  ctx.closePath();
  fillStroke(ctx, gradient, style.accent, 3);

  ctx.fillStyle = "rgba(255,255,255,0.30)";
  ctx.beginPath();
  ctx.ellipse(27, 22, 11, 5, -0.45, 0, Math.PI * 2);
  ctx.fill();
  drawEye(ctx, 25, 34, 7.2, 0.1, 0.18);
  drawEye(ctx, 40, 33, 7.2, 0.1, 0.18);
}

function drawRoundedStar(ctx: CanvasRenderingContext2D, style: PieceStyle, pose: number): void {
  const points: Array<[number, number]> = [];
  for (let i = 0; i < 50; i += 1) {
    const angle = -Math.PI / 2 + (i / 50) * Math.PI * 2;
    const lobe = (1 + Math.cos((angle + Math.PI / 2) * 5)) / 2;
    const radius = 25.4 + lobe * (pose === 1 ? 1.5 : pose === 2 ? 2.2 : 1.8);
    points.push([32 + Math.cos(angle) * radius, 32 + Math.sin(angle) * radius]);
  }
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i += 1) {
    const [x, y] = points[i];
    const [nx, ny] = points[(i + 1) % points.length];
    ctx.quadraticCurveTo(x, y, (x + nx) / 2, (y + ny) / 2);
  }
  ctx.closePath();
  fillStroke(ctx, pieceGradient(ctx, style, 5, 58), style.accent, 3);
  drawGloss(ctx, 19, 13, 25, 16);
  drawEye(ctx, 25, 27.3, 7.1, 0.16, 0.14);
  drawEye(ctx, 39, 27.3, 7.1, 0.16, 0.14);
}

function drawPurpleSqueeze(ctx: CanvasRenderingContext2D, style: PieceStyle, pose: number): void {
  const lean = [-1, 2, -2][pose] ?? 0;
  ctx.save();
  ctx.globalAlpha *= 0.78;
  const gradient = ctx.createRadialGradient(28 + lean, 18, 5, 34, 35, 35);
  gradient.addColorStop(0, "rgba(255,255,255,0.78)");
  gradient.addColorStop(0.18, style.light);
  gradient.addColorStop(0.45, style.base);
  gradient.addColorStop(0.82, "#7448cf");
  gradient.addColorStop(1, style.dark);
  ctx.beginPath();
  ctx.bezierCurveTo(15 + lean, 12, 45 + lean, 6, 52 + lean, 25);
  ctx.bezierCurveTo(59 + lean, 45, 45 - lean, 58, 27 - lean, 54);
  ctx.bezierCurveTo(8, 51, 7, 28, 15, 12);
  ctx.closePath();
  fillStroke(ctx, gradient, style.accent, 3);
  ctx.fillStyle = "rgba(255,255,255,0.16)";
  ctx.beginPath();
  ctx.bezierCurveTo(20 + lean, 17, 42 + lean, 13, 47 + lean, 27);
  ctx.bezierCurveTo(52 + lean, 43, 39 - lean, 51, 27 - lean, 48);
  ctx.bezierCurveTo(17, 46, 14, 29, 20, 17);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = "rgba(255,255,255,0.42)";
  ctx.beginPath();
  ctx.ellipse(29, 22, 15, 6, -0.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  for (const [x, y, r] of [
    [45, 23, 1.8],
    [20, 38, 1.4],
    [38, 48, 1.3],
    [25, 18, 1.2],
    [34, 28, 0.9],
    [49, 38, 1.0],
    [29, 51, 0.8]
  ]) {
    ctx.beginPath();
    ctx.moveTo(x, y - r * 2);
    ctx.lineTo(x + r, y);
    ctx.lineTo(x, y + r * 2);
    ctx.lineTo(x - r, y);
    ctx.closePath();
    ctx.fill();
  }
  drawEye(ctx, 26, 32, 7.3, 0.08, 0.16);
  drawEye(ctx, 41, 33, 7.3, 0.08, 0.16);
}

function drawGraySkeleton(ctx: CanvasRenderingContext2D, style: PieceStyle, pose: number): void {
  const wave = [0, -3, 2][pose] ?? 0;
  drawBoneLine(ctx, style, 24, 39, 15, 31 + wave, 4.2);
  drawBoneLine(ctx, style, 15, 31 + wave, 9, 25 + wave, 3.8);
  drawBoneLine(ctx, style, 40, 39, 49, 42 - wave, 4.2);
  drawBoneLine(ctx, style, 49, 42 - wave, 55, 34 - wave, 3.8);
  drawBoneLine(ctx, style, 27, 49, 22, 58, 4.8);
  drawBoneLine(ctx, style, 37, 49, 43, 58, 4.8);
  drawBoneLine(ctx, style, 32, 34, 32, 49, 3.2);

  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = style.accent;
  ctx.lineWidth = 4.2;
  ctx.beginPath();
  ctx.moveTo(30.5, 41.1);
  ctx.bezierCurveTo(27.2, 42, 23.9, 41.1, 21.4, 39.4);
  ctx.moveTo(33.5, 41.1);
  ctx.bezierCurveTo(36.8, 42, 40.1, 41.1, 42.6, 39.4);
  ctx.moveTo(30.5, 45.6);
  ctx.bezierCurveTo(26.9, 46.5, 23.5, 45.4, 20.9, 42.9);
  ctx.moveTo(33.5, 45.6);
  ctx.bezierCurveTo(37.1, 46.5, 40.5, 45.4, 43.1, 42.9);
  ctx.stroke();
  ctx.strokeStyle = style.light;
  ctx.lineWidth = 2.3;
  ctx.beginPath();
  ctx.moveTo(30.5, 41.1);
  ctx.bezierCurveTo(27.2, 42, 23.9, 41.1, 21.4, 39.4);
  ctx.moveTo(33.5, 41.1);
  ctx.bezierCurveTo(36.8, 42, 40.1, 41.1, 42.6, 39.4);
  ctx.moveTo(30.5, 45.6);
  ctx.bezierCurveTo(26.9, 46.5, 23.5, 45.4, 20.9, 42.9);
  ctx.moveTo(33.5, 45.6);
  ctx.bezierCurveTo(37.1, 46.5, 40.5, 45.4, 43.1, 42.9);
  ctx.stroke();
  ctx.restore();

  ctx.beginPath();
  roundedRect(ctx, 23, 47, 18, 6, 4);
  fillStroke(ctx, pieceGradient(ctx, style, 43, 55), style.accent, 1.6);

  ctx.beginPath();
  ctx.moveTo(16, 21);
  ctx.bezierCurveTo(16, 9, 24, 3, 32, 3);
  ctx.bezierCurveTo(42, 3, 49, 11, 49, 22);
  ctx.bezierCurveTo(49, 30, 44, 34, 42, 38);
  ctx.lineTo(22, 38);
  ctx.bezierCurveTo(19, 34, 16, 30, 16, 21);
  ctx.closePath();
  fillStroke(ctx, pieceGradient(ctx, style, 2, 41), style.accent, 3);
  drawGloss(ctx, 20, 8, 23, 14);
  drawOvalEye(ctx, 25.5, 21.5, 7.7, 9.3, 0.1, 0.12);
  drawOvalEye(ctx, 38.8, 21.5, 7.7, 9.3, 0.1, 0.12);

  ctx.strokeStyle = "rgba(255,255,255,0.46)";
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(22, 33);
  ctx.bezierCurveTo(27, 36, 37, 36, 42, 33);
  ctx.stroke();

  ctx.fillStyle = style.light;
  ctx.strokeStyle = style.accent;
  ctx.lineWidth = 1.2;
  for (const [x, y] of [
    [7.5, 24 + wave],
    [9.5, 28 + wave],
    [55.5, 33 - wave],
    [57.5, 37 - wave],
    [21, 60],
    [43, 60]
  ]) {
    ctx.beginPath();
    ctx.ellipse(x, y, 2.5, 2.8, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
}

export function drawPiece(
  ctx: CanvasRenderingContext2D,
  type: PieceType,
  x: number,
  y: number,
  size: number,
  options: PieceDrawOptions | boolean = {}
): void {
  const opts = normalizeOptions(options);
  const style = PIECE_STYLES[type];
  const phase = opts.phase ?? 0;
  const pose = Math.abs(Math.floor(phase / 180 + type)) % 3;
  const bob = opts.active ? Math.sin(phase * 0.007) * 1.2 : Math.sin(phase * 0.003 + type) * 0.45;
  const pulse = opts.active ? 1 + Math.sin(phase * 0.009) * 0.025 : 1;
  const squash = Math.max(-0.5, Math.min(1.4, opts.squash ?? 0));
  const scaleX = Math.max(0.72, 1 + squash * 0.36);
  const scaleY = Math.max(0.56, 1 - squash * 0.28);
  const impactY = squash > 0 ? squash * size * 0.075 : squash * size * 0.035;

  ctx.save();
  ctx.translate(x + size / 2, y + size / 2 + bob + impactY);
  ctx.scale((size / 64) * pulse * scaleX, (size / 64) * pulse * scaleY);
  ctx.translate(-32, -32);

  if (opts.ghost) {
    ctx.globalAlpha = 0.1;
  }

  ctx.save();
  ctx.shadowColor = style.glow;
  ctx.shadowBlur = opts.active ? 18 : 8;
  drawShadow(ctx);
  if (type === 1) {
    drawBlueBat(ctx, style, pose);
  } else if (type === 2) {
    drawRedCrab(ctx, style, pose);
  } else if (type === 3) {
    drawGreenSlime(ctx, style, pose);
  } else if (type === 4) {
    drawRoundedStar(ctx, style, pose);
  } else if (type === 5) {
    drawPurpleSqueeze(ctx, style, pose);
  } else {
    drawGraySkeleton(ctx, style, pose);
  }
  ctx.restore();

  if (opts.highContrast) {
    drawHighContrastMark(ctx, type);
  }

  ctx.restore();
}

export function drawPiecePreview(
  ctx: CanvasRenderingContext2D,
  upper: PieceType,
  lower: PieceType,
  width: number,
  height: number,
  highContrast = false
): void {
  ctx.clearRect(0, 0, width, height);
  const background = ctx.createLinearGradient(0, 0, width, height);
  background.addColorStop(0, "#14242d");
  background.addColorStop(1, "#0c1116");
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.strokeRect(0.5, 0.5, width - 1, height - 1);

  const size = Math.min(width * 0.78, height * 0.42);
  const x = (width - size) / 2;
  drawPiece(ctx, upper, x, height * 0.06, size, { highContrast, active: true, phase: performance.now() });
  drawPiece(ctx, lower, x, height * 0.52, size, { highContrast, active: true, phase: performance.now() + 150 });
}
